<script setup lang="ts">
import { computed, resolveComponent } from 'vue';
import type { THEME } from '@pkg/config/misc/theme';
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import type { PantryButtonConfig } from '@pkg/ui/components/ui/button';
import { OBJECT } from '@pkg/config/sanity/schemas';
import type { AllButton } from '@pkg/sanity-utils/types/button';
import { APP_STORE_LINK_TYPE } from '@pkg/sanity-utils/constants/appstore';
import { analyticsClickBehaviourForGeneralLink } from '@pkg/sanity-utils/constants/analyticsBehaviour';
import { buttonRecipe } from '@pkg/ui/components/ui/button/buttonRecipe';
import { BaseButtonGroup } from '@pkg/ui/components/base/BaseButtonGroup';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import { buttonToLink } from '@pkg/link';

interface Props {
  theme?: THEME;
  buttons: Array<AllButton>;
  groupClass?: string;
  class?: string;
  buttonClass?: string;
  config?: PantryButtonConfig;
}

const functionalButtonHandler = (button: any) => {
  if (button.buttonClickCallback && !button.disabled) {
    button.buttonClickCallback();
  };
};
const props = defineProps<Props>();

const buttonRecipeClass = computed(() => {
  const { recipeVariant: variant, size = 'large' } = props.config ?? {};
  return buttonRecipe({ variant, size });
});

const device = useDevice();
const isDesktop = computed(() => device.isDesktop);

/** Host app registers these (apps/web base components). */
const AutomaticAppStore = resolveComponent('AutomaticAppStore');
const AppStore = resolveComponent('AppStore');
</script>

<template>
  <BaseButtonGroup :data-theme="props.theme" :class="props.groupClass">
    <template v-for="button of props.buttons" :key="button._key">
      <CustomLink
        v-if="button._type === OBJECT.NAVIGATION_LINK_INTERNAL || button._type === OBJECT.NAVIGATION_LINK_EXTERNAL || button._type === OBJECT.NAVIGATION_LINK_SSU"
        :link="button"
        :class="[buttonRecipeClass, 'c-button-group-link', props.class]"
      >
        {{ button.title }}
      </CustomLink>

      <CustomLink
        v-else-if="button._type === OBJECT.BUTTON"
        :link="buttonToLink(button)"
        :title="button.title"
        :data-analytics-click-behaviour="analyticsClickBehaviourForGeneralLink(button.link)"
        :button-click-callback="button.buttonClickCallback"
        :class="[buttonRecipeClass, 'c-button-group-link', props.class]"
      >
        <span class="button-text">
          {{ button.title }}
        </span>
      </CustomLink>

      <template v-else-if="button._type === OBJECT.APPSTORE_LINK">
        <component
          :is="AutomaticAppStore"
          v-if="button.variant === APP_STORE_LINK_TYPE.AUTOMATIC && button.buttonText"
          :key="`automatic-${button._key}`"
          class="amp-auto-track"
          :class="props.class"
          :config="props.config"
          :data-analytics-click-behaviour="!isDesktop ? AnalyticsClickBehaviour.Download : AnalyticsClickBehaviour.DownloadModal"
        >
          {{ button.buttonText }}
        </component>
        <component
          :is="AppStore"
          v-else-if="button.variant === APP_STORE_LINK_TYPE.SPECIFIC && button.appStore"
          :key="`appstore-${button._key}`"
          :app-store="button.appStore"
          :data-analytics-click-behaviour="!isDesktop ? AnalyticsClickBehaviour.Download : AnalyticsClickBehaviour.DownloadModal"
          :button-color="button.buttonColor || 'white'"
          :class="[props.class]"
        />
      </template>
      <template v-else-if="button._type === OBJECT.LINK_FUNCTIONAL">
        <UiButton
          :class="props.buttonClass"
          :disabled="button.disabled"
          :config="props.config"
          class="amp-auto-track"
          @click.stop="functionalButtonHandler(button)"
        >
          {{ button.title }}
        </UiButton>
      </template>
    </template>
  </BaseButtonGroup>
</template>
